import React, {useCallback, useMemo} from "react";
import {HierarchyEmployee} from "../../util/EmployeeHierarchy";
import {
    Autocomplete,
    AutocompleteRenderOptionState,
    Box,
    Checkbox,
    ListItemText,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {autocompleteChangeEvent} from "../../util/FormHandlers";
import {AutocompleteRenderInputParams} from "@mui/material/Autocomplete/Autocomplete";
import NeighbourhoodColorIcon from "../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import {HoodItem, SeatingPlanModel} from "./SeatingPlanModel";
import {usePermission} from "../../util/access/UseAccess";
import {SeatingPlans_Admin} from "../../util/access/Permission";
import {UNASSIGNED_NEIGHBOURHOOD_ID} from "../../api/Model";


const renderValue = (values: HoodItem[]) => (
    <Stack direction="row" spacing={1} alignItems='center' flexWrap="nowrap" maxWidth="calc(100% - 40px)">
        {values.map(hood => (
            <Box
                alignSelf="center"
                key={hood.key}
            >
                <NeighbourhoodColorIcon
                    key={hood.key}
                    neighbourhood={hood}
                />
            </Box>
        ))}
        <Typography variant='body2' sx={{
            textWrap: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
        }}>{values.length === 0 ? (
            ''
        ) : values.length === 1 ? (
            values[0].name
        ) : (
            `${values.length} neighbourhoods`
        )}
        </Typography>
    </Stack>
);

export function SeatingPlanNeighbourhoodSelect({node}: {
    node: HierarchyEmployee,
}) {
    const {model} = React.useContext(EmployeeTreeTableContext);
    if (!model) throw new Error('Seating plan not found');
    const {canAdmin} = usePermission({canAdmin: SeatingPlans_Admin});

    const hoodItems = useMemo(
        () => model.modifiedEmployeeNeighbourhood.get(node)
            .filter(hood => hood.id !== UNASSIGNED_NEIGHBOURHOOD_ID),
        [node, model]
    );

    const renderOption = useCallback(({key, ...props}: React.HTMLAttributes<HTMLLIElement> & {
        key: any
    }, option: HoodItem, {selected}: AutocompleteRenderOptionState) => (
        <li key={key} {...props}>
            <Checkbox checked={selected} style={{marginRight: 8}}/>
            <ListItemText primary={option.name}/>
            {model.isMoved({employee: node, hood: option}) && (
                <Tooltip title="Modified">
                    <AgricultureIcon color="warning" fontSize="small"/>
                </Tooltip>
            )}
        </li>
    ), [model, node]);

    const handleNeighbourhoodChange = useMemo(
        () => autocompleteChangeEvent(
            (values: HoodItem[]) => model.moveEmployee(node, values)
        ),
        [model, node],
    );

    const renderInput = useCallback((params: AutocompleteRenderInputParams) => (
        <TextField {...params} variant="standard"/>
    ), []);

    return (
        <Autocomplete
            multiple
            fullWidth
            disableCloseOnSelect
            options={model.assignableHoodItems}
            value={hoodItems}
            disabled={!canAdmin}
            onChange={handleNeighbourhoodChange}
            getOptionLabel={option => option.name}
            isOptionEqualToValue={SeatingPlanModel.isEqualHood}
            renderOption={renderOption}
            renderValue={renderValue}
            renderInput={renderInput}
        />

    );
}

export const EmployeeTreeTableContext = React.createContext<{ model?: SeatingPlanModel }>({});
